import { IconName } from "@components/AnimalCard";

type DynamicItem = {
  type: IconName;
  uid: number;
  pet_id?: string | null;
};


export function syncAgentConsent(items: DynamicItem[], watch: any, setValue: any): boolean {
  let cleared = false;
  items.forEach(item => {
    const consentValue = watch(`${item.type}.agent_consent.${item.uid}`);
    const agentCondition = watch(`${item.type}.agent_consent_conditions.${item.uid}`);

    // When "I do not consent" is selected, clear all consent-related fields
    if (consentValue === "i_don_not_consent") {
      cleared = true;
      if (agentCondition !== "") {
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "", { shouldValidate: false });
      }
      // Clear consent-related fields that shouldn't show when declining
      setValue(`${item.type}.other_reasonable_conditions.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.condisions_details.${item.uid}`, "", { shouldValidate: false });
      setValue(`${item.type}.clause_57.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.clause_58.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.clause_59.${item.uid}`, false, { shouldValidate: false });
    }

    // When "I consent" is selected, clear all decline-related fields
    if (consentValue === "i_consent") {
      cleared = true;
      // Clear all refusal reason checkboxes
      setValue(`${item.type}.unreasonable_number_of_animala.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.fencing.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.open_space.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.welfare.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.damage.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.other_laws.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.landlord_lives_at_property.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.tenant_did_not_agree.${item.uid}`, false, { shouldValidate: false });
      // Clear the explanation textarea
      setValue(`${item.type}.selected_reason_for_apply.${item.uid}`, "", { shouldValidate: false });
      // Clear the "I have read" checkbox for decline
      setValue(`${item.type}.conditions_tenants.${item.uid}`, false, { shouldValidate: false });
    }

    // When "No conditions" is selected, clear all clause fields and the controlling checkbox
    if (agentCondition === "i_consent_no_conditions") {
      cleared = true;
      setValue(`${item.type}.clause_57.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.clause_58.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.clause_59.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.other_reasonable_conditions.${item.uid}`, false, { shouldValidate: false });
      setValue(`${item.type}.condisions_details.${item.uid}`, "", { shouldValidate: false });
    }
  });
  return cleared;
}

export function syncLandlordDetails(agentApprovedlandlord: any, reset: any) {
  if (agentApprovedlandlord) {
    reset({
      landlords_first_name: agentApprovedlandlord.first_name || "",
      landlords_last_name: agentApprovedlandlord.last_name || "",
      landlords_email: agentApprovedlandlord.email || "",
      landlords_phone: agentApprovedlandlord.phone || ""
    });
  }
}

export function syncAgentApprovedApplications(agentApprovedApplications: any, setValue: any) {
  setValue(`conditions_tenants`, agentApprovedApplications?.conditions_discussed || "");
  setValue(`general_consent`, agentApprovedApplications?.general_consent || "");
}

export function syncAgentApprovedPetsDetails(agentApprovedPetsDetails: any[], items: DynamicItem[], setValue: any) {
  if (agentApprovedPetsDetails && agentApprovedPetsDetails.length > 0 && items.length > 0) {
    items.forEach(item => {
      const petDetails = agentApprovedPetsDetails.find(pet => pet.id === item.pet_id)?.agent_comments?.[0];

      if (petDetails) {
        setValue(`${item.type}.agent_consent.${item.uid}`, petDetails.agent_status === "approved" ? "i_consent" : petDetails.agent_status === "declined" ? "i_don_not_consent" : "");
        setValue(`${item.type}.agent_consent_conditions.${item.uid}`, petDetails?.has_conditions? "i_consent_with_conditions" : "");
        setValue(`${item.type}.agent_comments.${item.uid}`, petDetails.agent_comments || "");
        setValue(`${item.type}.agent_decline_reasons.${item.uid}`, petDetails.agent_decline_reasons || []);
        setValue(`${item.type}.agent_decline_explanation.${item.uid}`, petDetails.agent_decline_explanation || "");
        setValue(`${item.type}.conditions_tenants.${item.uid}`, petDetails.agent_discussed_with_tenant);
        setValue(`${item.type}.pet_keeping_conditions.${item.uid}`, petDetails.pet_keeping_conditions || "");
        setValue(`${item.type}.pet_condition.${item.uid}`, petDetails.pet_condition || "");
        setValue(`${item.type}.pet_condition_reason.${item.uid}`, petDetails.pet_condition_reason || "");

        if (petDetails.agent_status === "declined") {
          const declinedResons = petDetails?.agent_decline_reasons || [];
          if (declinedResons?.length > 0) {
            // NSW reasons
            setValue(`${item.type}.unreasonable_number_of_animala.${item.uid}`, declinedResons?.includes("unreasonable_number"));
            setValue(`${item.type}.fencing.${item.uid}`, declinedResons?.includes("fencing"));
            setValue(`${item.type}.open_space.${item.uid}`, declinedResons?.includes("open_space"));
            setValue(`${item.type}.welfare.${item.uid}`, declinedResons?.includes("welfare"));
            setValue(`${item.type}.damage.${item.uid}`, declinedResons?.includes("damage"));
            setValue(`${item.type}.other_laws.${item.uid}`, declinedResons?.includes("other_laws"));
            setValue(`${item.type}.landlord_lives_at_property.${item.uid}`, declinedResons?.includes("landlord_lives_here"));
            setValue(`${item.type}.tenant_did_not_agree.${item.uid}`, declinedResons?.includes("tenant_disagree"));
            // QLD reasons
            setValue(`${item.type}.qld_unreasonable_number.${item.uid}`, declinedResons?.includes("qld_unreasonable_number"));
            setValue(`${item.type}.qld_unsuitable_premises.${item.uid}`, declinedResons?.includes("qld_unsuitable_premises"));
            setValue(`${item.type}.qld_likely_damage.${item.uid}`, declinedResons?.includes("qld_likely_damage"));
            setValue(`${item.type}.qld_health_safety_risk.${item.uid}`, declinedResons?.includes("qld_health_safety_risk"));
            setValue(`${item.type}.qld_contravene_law.${item.uid}`, declinedResons?.includes("qld_contravene_law"));
            setValue(`${item.type}.qld_body_corporate_bylaw.${item.uid}`, declinedResons?.includes("qld_body_corporate_bylaw"));
            setValue(`${item.type}.qld_tenant_not_agreed_conditions.${item.uid}`, declinedResons?.includes("qld_tenant_not_agreed_conditions"));
            setValue(`${item.type}.qld_not_a_pet.${item.uid}`, declinedResons?.includes("qld_not_a_pet"));
            setValue(`${item.type}.qld_licence_condition.${item.uid}`, declinedResons?.includes("qld_licence_condition"));
            setValue(`${item.type}.qld_another_ground.${item.uid}`, declinedResons?.includes("qld_another_ground"));
            setValue(`${item.type}.selected_reason_for_apply.${item.uid}`, petDetails?.agent_decline_explanation);
          }
        }

        if (petDetails?.has_conditions === false) {
          setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "i_consent_no_conditions");
        } else if (petDetails?.has_conditions === true) {
          const agentStandardConditions = petDetails?.agent_standard_conditions || [];
          setValue(`${item.type}.agent_consent_conditions.${item.uid}`, "i_consent_conditions_apply");
          setValue(`${item.type}.clause_57.${item.uid}`, agentStandardConditions?.includes("clause_57"));
          setValue(`${item.type}.clause_58.${item.uid}`, agentStandardConditions?.includes("clause_58"));
          setValue(`${item.type}.clause_59.${item.uid}`, agentStandardConditions?.includes("clause_59"));
          setValue(`${item.type}.condisions_details.${item.uid}`, petDetails?.agent_custom_conditions || "");
        }
      }
    });
  }
}